import { ArrowRight, ArrowUpRight, Clock, User } from "lucide-react";
import Reveal from "./Reveal";
import { placeholder } from "../../lib/constants";

const COLORS = {
  bg: "#F5F1EC",
  card: "#FAF8F5",
  border: "#D8C7B5",
  text: "#2B241F",
  muted: "#8C8178",
  gold: "#C8A97E",
  goldLight: "#E8D5B0",
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  try { return new Date(dateStr).toLocaleDateString("pt-PT", { day: "numeric", month: "long", year: "numeric" }); } catch { return dateStr; }
}

function monthYear(dateStr) {
  if (!dateStr) return "";
  try { const d = new Date(dateStr); return d.toLocaleDateString("pt-PT", { month: "short", year: "numeric" }).toUpperCase(); } catch { return ""; }
}

function estReadTime(content) {
  if (!content) return "1 MIN";
  const text = content.replace(/<[^>]*>/g, "");
  const words = text.trim().split(/\s+/).length;
  return `${Math.max(1, Math.ceil(words / 200))} MIN`;
}

function AuthorPill({ name, avatar }) {
  if (!name) return null;
  return (
    <span className="inline-flex items-center gap-1.5 shrink-0" style={{ color: COLORS.muted }}>
      {avatar ? (
        <img src={avatar} alt={name} className="w-4 h-4 rounded-full object-cover" />
      ) : (
        <span className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-medium uppercase" style={{ backgroundColor: "rgba(200,169,126,0.15)", color: COLORS.gold }}>
          {name[0]}
        </span>
      )}
      <span className="text-[10px] tracking-[0.12em]">{name}</span>
    </span>
  );
}

function ReadTimePill({ content }) {
  const mins = estReadTime(content);
  return (
    <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-[9px] tracking-[0.12em] uppercase rounded-full" style={{ backgroundColor: "rgba(200,169,126,0.10)", color: COLORS.gold }}>
      <Clock size={10} /> {mins}
    </span>
  );
}

export default function Blog({ items, sectionContent, uiStrings, sectionId }) {
  const bs = uiStrings?.blog_section || {};
  const eyebrow = bs.eyebrow || sectionContent?.eyebrow || "PUBLICAÇÕES";
  const headline = bs.headline || sectionContent?.headline || "Perspectivas & Reflexões";
  const description = bs.description || sectionContent?.description || "Artigos, análises e reflexões profundas sobre temas que nos inspiram.";
  const readLabel = bs.read_more || "LER ARTIGO";
  const seeAllLabel = "VER TODOS OS ARTÍCULOS";

  const published = (items || []).filter((i) => i.published === 1 || i.published === true);
  if (published.length === 0) return null;

  const featured = published[0];
  const recent = published.slice(1);

  return (
    <section data-id="site-blog" id={sectionId} style={{ backgroundColor: COLORS.bg }} className="py-24 md:py-36">
      <div data-id="blog-container" className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <div data-id="blog-header" className="grid lg:grid-cols-12 gap-8 md:gap-16 mb-16 md:mb-20">
            <div className="lg:col-span-5">
              <p className="text-[10px] md:text-[11px] tracking-[0.32em] uppercase font-medium mb-4 md:mb-5" style={{ color: COLORS.gold }} data-id="blog-eyebrow">{eyebrow}</p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.08] font-light tracking-[-0.01em]" style={{ color: COLORS.text }} data-id="blog-headline">{headline}</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-sm sm:text-base leading-relaxed font-light max-w-md" style={{ color: COLORS.muted }} data-id="blog-description">{description}</p>
            </div>
          </div>
        </Reveal>

        <div className="w-full h-px mb-16 md:mb-20" style={{ backgroundColor: COLORS.border }} />

        {/* Featured article */}
        <Reveal>
          <a href={`/blog/${featured.slug}`} data-id={`blog-featured-${featured.uid}`} className="group block mb-20 md:mb-28">
            <article className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
              <div data-id="blog-featured-image" className="relative overflow-hidden aspect-[16/10] lg:aspect-[16/9] order-2 lg:order-1" style={{ backgroundColor: COLORS.border }}>
                {featured.image_url ? (
                  <img src={featured.image_url} alt={featured.title} className="w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-[1.02]" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'rgba(200,169,126,0.08)' }}>
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke={COLORS.border} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                  </div>
                )}
                {featured.category && (
                  <span className="absolute top-4 left-4 px-3 py-1.5 text-[9px] tracking-[0.22em] uppercase" style={{ backgroundColor: COLORS.card, color: COLORS.gold, border: `1px solid ${COLORS.border}` }}>{featured.category}</span>
                )}
                <span className="absolute top-4 right-4 px-2.5 py-1 text-[9px] tracking-[0.12em] uppercase rounded-full" style={{ backgroundColor: "rgba(200,169,126,0.10)", color: COLORS.gold }}><Clock size={10} className="inline mr-1" />{estReadTime(featured.content)}</span>
              </div>

              <div data-id="blog-featured-content" className="order-1 lg:order-2">
                <div className="flex items-center gap-3 text-[10px] tracking-[0.18em] uppercase mb-4 md:mb-6 flex-wrap" style={{ color: COLORS.muted }}>
                  <span>{formatDate(featured.created_at)}</span>
                  <AuthorPill name={featured.author_name} avatar={featured.author_avatar} />
                </div>

                <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-[2.25rem] leading-[1.1] font-light tracking-[-0.01em] mb-4 md:mb-6 group-hover:opacity-80 transition-opacity duration-500" style={{ color: COLORS.text }}>{featured.title}</h3>

                {featured.excerpt && (
                  <p className="text-sm sm:text-base leading-relaxed font-light mb-6 md:mb-8 max-w-lg" style={{ color: COLORS.muted }}>{featured.excerpt}</p>
                )}

                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-medium group-hover:gap-3 transition-all duration-500" style={{ color: COLORS.gold }}>{readLabel}<ArrowUpRight size={13} strokeWidth={1.5} /></span>
              </div>
            </article>
          </a>
        </Reveal>

        {/* Recent Articles */}
        {recent.length > 0 && (
          <div data-id="blog-recent" className="mb-16 md:mb-20">
            <p className="text-[10px] md:text-[11px] tracking-[0.28em] uppercase font-medium mb-10 md:mb-14" style={{ color: COLORS.gold }}>ARTIGOS RECENTES</p>

            <div className="space-y-0">
              {recent.map((p, idx) => {
                const num = String(idx + 2).padStart(2, "0");
                return (
                  <Reveal key={p.uid} delay={idx * 80}>
                    <a href={`/blog/${p.slug}`} data-id={`blog-recent-${p.uid}`} className="group block">
                      <article className="grid gap-4 md:gap-10 items-center py-6 md:py-8 transition-colors duration-300 hover:bg-black/[0.01] -mx-4 px-4 rounded-sm"
                        style={{ borderTop: idx === 0 ? `1px solid ${COLORS.border}` : "none", borderBottom: `1px solid ${COLORS.border}`, gridTemplateColumns: "48px 1fr 80px" }}>
                        <div className="hidden sm:block">
                          <span className="font-serif text-lg md:text-xl tracking-tight" style={{ color: COLORS.gold }}>{num}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-[9px] md:text-[10px] tracking-[0.16em] uppercase mb-1.5 md:mb-2 flex-wrap" style={{ color: COLORS.muted }}>
                            <span>{monthYear(p.created_at)}</span>
                            <AuthorPill name={p.author_name} avatar={p.author_avatar} />
                          </div>
                          <h4 className="font-serif text-base sm:text-lg md:text-xl leading-snug font-light tracking-[-0.01em] group-hover:opacity-80 transition-opacity duration-500" style={{ color: COLORS.text }}>{p.title}</h4>
                          {p.excerpt && <p className="text-xs sm:text-sm leading-relaxed font-light mt-1.5 md:mt-2 line-clamp-2" style={{ color: COLORS.muted }}>{p.excerpt}</p>}
                        </div>
                        <div className="shrink-0 w-20 h-16 md:w-24 md:h-20 rounded-sm overflow-hidden relative" style={{ backgroundColor: COLORS.border }}>
                          {p.image_url ? (
                            <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'rgba(200,169,126,0.06)' }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={COLORS.border} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                            </div>
                          )}
                          {p.category && (
                            <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 text-[7px] tracking-[0.16em] uppercase rounded-sm" style={{ backgroundColor: "rgba(43,36,31,0.75)", color: COLORS.goldLight }}>{p.category}</span>
                          )}
                          <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 text-[7px] tracking-[0.1em] uppercase rounded-sm" style={{ backgroundColor: "rgba(200,169,126,0.85)", color: COLORS.text }}>{estReadTime(p.content)}</span>
                        </div>
                      </article>
                    </a>
                  </Reveal>
                );
              })}
            </div>
          </div>
        )}

        {published.length > 0 && (
          <Reveal>
            <div className="flex justify-center pt-4" data-id="blog-see-all">
              <a href="/#blog" className="inline-flex items-center gap-3 px-10 py-4 text-[10px] tracking-[0.22em] uppercase font-medium rounded-full border transition-all duration-500 hover:gap-4"
                style={{ color: COLORS.text, borderColor: COLORS.border, backgroundColor: COLORS.card }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = COLORS.text; e.target.style.color = COLORS.card; e.target.style.borderColor = COLORS.text; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = COLORS.card; e.target.style.color = COLORS.text; e.target.style.borderColor = COLORS.border; }}>
                {seeAllLabel}<ArrowRight size={14} strokeWidth={1.5} />
              </a>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
